export type QuickAnswerItem = {
  question: string
  answer: {
    paragraphs?: string[]
    orderedList?: string[]
    unorderedList?: string[]
  }
}

export type QuickAnswerSection = {
  heading: string
  items: QuickAnswerItem[]
}

export const QUICK_ANSWER_SECTIONS: QuickAnswerSection[] = [
  {
    heading: "Getting Started",
    items: [
      {
        question: "What is Signature Stay Collection?",
        answer: {
          paragraphs: [
            "Signature Stay Collection is a curated selection of professionally managed vacation homes available worldwide - including condos, cabins, beachfront homes, and luxury villas across top destinations in the U.S. and more than 80 countries.",
          ],
        },
      },
      {
        question: "Is there a cost to create an account?",
        answer: { paragraphs: ["No. Creating an account is completely free."] },
      },
    ],
  },
  {
    heading: "Booking a Property",
    items: [
      {
        question: "What is the minimum age to book?",
        answer: {
          paragraphs: [
            "Most properties require travelers to be at least 18 years old, though some hosts require 21 or 25. Always check the house rules on the listing page before booking. The property manager may ask to see your ID at check-in.",
          ],
        },
      },
      {
        question: "How do I find pet-friendly properties?",
        answer: {
          paragraphs: [
            "When searching, click Add guests and toggle the Pets option, then click Apply and Search to see pet-friendly listings. Check each listing for specific pet policies or fees before booking.",
          ],
        },
      },
      {
        question: "What if I want to add a pet after booking?",
        answer: {
          paragraphs: [
            "Contact us at help@signaturestaycollection.com and we'll coordinate with the host to update the reservation and confirm any applicable fees or rules.",
          ],
        },
      },
      {
        question: "What about service animals?",
        answer: {
          paragraphs: [
            "Service animals are welcome at all Signature Stay Collection properties, even those with no-pet policies. We recommend notifying the property manager in advance so everything is ready for your arrival.",
          ],
        },
      },
      {
        question: "Can I change the number of guests on a booking?",
        answer: {
          paragraphs: [
            "Yes. Contact us and we'll work with the host to update the guest count. Properties have maximum occupancy limits and some charge additional fees for extra travelers.",
          ],
        },
      },
      {
        question: "Can I change the name on a booking?",
        answer: {
          paragraphs: [
            "No. Once a booking is complete, the name is locked and cannot be transferred. If the original traveler can no longer make the trip, contact the host directly for assistance.",
          ],
        },
      },
      {
        question: "Are these the same homes found on other platforms?",
        answer: {
          paragraphs: [
            "In some cases, yes. All homes in Signature Stay Collection are professionally managed and vetted for quality and reliability.",
          ],
        },
      },
    ],
  },
  {
    heading: "Managing Your Booking",
    items: [
      {
        question: "How do I view my bookings?",
        answer: {
          paragraphs: [
            "Click your name in the upper right corner of the page, scroll down, and select Bookings. You'll see all current and past bookings with details for each.",
          ],
        },
      },
      {
        question: "How do I share my itinerary?",
        answer: {
          paragraphs: [
            "After completing a booking, you'll see an option to share the itinerary by email. You can also go to My Bookings, select the trip, click Add guest, and enter the email addresses of the people traveling with you.",
          ],
        },
      },
      {
        question: "Can I change the dates of a booking?",
        answer: {
          paragraphs: [
            "Yes, but date changes require host approval. Email support@whimstay.com as soon as possible and our team will coordinate with the host on your behalf. Pricing adjustments may apply.",
          ],
        },
      },
      {
        question: "How do I request an early check-in or late checkout?",
        answer: {
          paragraphs: [
            "Contact the host directly - you'll find their information in your booking details and confirmation emails. The sooner you ask, the better. Early check-ins and late checkouts aren't guaranteed, so have a backup plan ready.",
          ],
        },
      },
    ],
  },
  {
    heading: "Check-In",
    items: [
      {
        question: "How do I find my check-in details?",
        answer: {
          paragraphs: ["Check-in details are delivered in a few ways:"],
          orderedList: [
            "Booking confirmation email - sent right after booking with the property address, check-in and checkout times, and other key details.",
            "Host email - detailed check-in instructions (including key or door code info) sent by the host 24-48 hours before arrival.",
            "If instructions haven't arrived - check your spam folder first, then contact the host directly using the information in your booking confirmation.",
          ],
        },
      },
      {
        question: "Do I need to sign a rental agreement?",
        answer: {
          paragraphs: [
            "Some property managers require a rental agreement before check-in. If you have questions about the terms, contact the property manager directly - their information is in your booking details and confirmation emails.",
          ],
        },
      },
    ],
  },
  {
    heading: "Cancellations & Refunds",
    items: [
      {
        question: "What is the cancellation policy?",
        answer: {
          paragraphs: [
            "Cancellation policies are set by each host and vary by property. You can find the specific policy on the property details page, in your booking confirmation email, and in the My Bookings section of your account.",
          ],
        },
      },
      {
        question: "How do I cancel a booking?",
        answer: {
          paragraphs: [
            "The cancellation screen will show your refund amount, any fees withheld, and your total refund.",
          ],
          orderedList: [
            "Go to your Bookings page",
            "Select the booking you want to cancel",
            "Under Change booking, click Modify booking",
            "Select the reason for cancellation",
            "Review your refund details and confirm",
          ],
        },
      },
      {
        question: "What refund options are available?",
        answer: {
          paragraphs: ["When canceling, you'll have two options:"],
          unorderedList: [
            "USD refund - returned to your original payment method within 5-10 business days",
            "Signature Stay Collection credit - the full booking amount (minus travel protection if purchased) issued as stay credit toward a future booking; choosing this option waives the non-refundable cancellation fee",
          ],
        },
      },
      {
        question: "What if I cancel outside the refundable window?",
        answer: {
          paragraphs: [
            "Your booking will be canceled but no refund will be issued.",
          ],
        },
      },
      {
        question: "What if I booked within 72 hours of check-in?",
        answer: {
          paragraphs: [
            "These bookings can be canceled but are non-refundable.",
          ],
        },
      },
      {
        question: "What about travel protection?",
        answer: {
          paragraphs: [
            "Travel protection costs are not included in your cancellation refund or stay credit, but may be separately refundable - contact us for help. You can also add travel protection at checkout to extend your cancellation window to your check-in date.",
          ],
        },
      },
      {
        question: "How long does my refund take?",
        answer: {
          paragraphs: [
            "Refunds are returned to your original payment method and typically take 1-15 business days to appear, depending on your bank. If your original card or account is no longer accessible, contact your bank directly - they'll determine how to route the refund.",
          ],
        },
      },
    ],
  },
  {
    heading: "During Your Stay",
    items: [
      {
        question: "Who do I contact if something goes wrong?",
        answer: {
          paragraphs: [
            "Your first point of contact is always the property manager. Their information is in your booking confirmation and check-in emails. If you're unable to reach them or need additional help, contact us at help@signaturestaycollection.com.",
          ],
        },
      },
    ],
  },
]

export const QUICK_ANSWERS = QUICK_ANSWER_SECTIONS.flatMap((section) =>
  section.items.map((item) => ({
    question: item.question,
    answer: [
      ...(item.answer.paragraphs ?? []),
      ...(item.answer.orderedList ?? []),
      ...(item.answer.unorderedList ?? []),
    ].join(" "),
  })),
)
